import {Injectable, ChangeDetectorRef, ApplicationRef} from '@angular/core';
import {Subject} from "rxjs/Subject";
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from "rxjs";
import {
  PlayerControl,
  Storyboard,
  WaveData,
  Layer,
  Clip,
  Timeline, TextClip,
} from "storyboard-renderer";
import {Http} from "@angular/http";
import { StoryboardVersion } from 'app/shared/interface/story-board-version';

@Injectable()
export class TimelineService {

  private timelinePosition = 0;
  private storyboard: Storyboard;

  private zoom = 1;
  private zoomStep = 0.1;
  private maxZoom = 10;
  private minZoom = .2;
  private scrollEnabled = false;

  private pxTimeRatio = 25 / 1000; // 5 px is 1000 milliseconds
  private timelineWidthInPx = 600;

  public zoomChangeEvent = new Subject();
  public durationChangeEvent = new Subject();
  public storyboardChangeEvent = new Subject();
  public closeAllClipMenuEvent = new Subject();

  public storyboardSub = new Subject<Storyboard>();

  public waveData = new BehaviorSubject<WaveData>(null);

  public playerControl: PlayerControl;

  public isClipExtending = false;

  public previousVersions: string[] = [];

  constructor(protected http:Http, private ref: ApplicationRef) {}

  public setStoryboard(storyboard: Storyboard) {
    this.storyboard = storyboard;
    this.setPlayerControl(new PlayerControl(this.storyboard.duration));
    this.reloadStoryboardAttachedAudioWaveData(storyboard)
      .catch((err) => {
        console.log("reloadStoryboardAttachedAudioWaveData error",err);
      });
  }

  public reloadStoryboardAttachedAudioWaveData(storyboard: Storyboard = null):Promise<WaveData>{
    return new Promise<WaveData>((resolve, reject) => {
      if (!storyboard){
        storyboard = this.storyboard;
      }
      if (!storyboard){
        return reject(new Error("missing storyboard"));
      }
      let url:string;
      try {
        url = this.storyboard.attached_audio.media_waveform.url;
      }catch(e){
        return reject(new Error("missing storyboard attached audio waveform url"));
      }
      this.http.get(url)
        .toPromise()
        .then((res) => {
          this.waveData.next(WaveData.fromJson(res.text()));
          return res;
        })
        .catch(reject);

    })
  }

  public getStoryboard():Storyboard{
    return this.storyboard;
  }

  public getPlayerControl(): PlayerControl{
    return this.playerControl;
  }

  public setPlayerControl(value: PlayerControl) {
    this.playerControl = value;
  }

  public onClipChange(clip: Clip, newProperties: any) {
    if(!clip.unlocked && clip instanceof TextClip && clip.transcription) {
      this.storyboard.timeline.layers.forEach((layer: Layer) => {
        layer.clips.forEach((otherClip: Clip) => {

          if (otherClip instanceof TextClip && (otherClip as TextClip).transcription && !otherClip.unlocked) {
            otherClip.dimensions.position.y = newProperties.position.y;
            otherClip.dimensions.position.x = newProperties.position.x;
          }
        });
      });

      this.storyboardSub.next(this.storyboard);
    }
  }

  public addCurrentVersionToHistory(fromRender = false, storyboard = this.storyboard): void {
    if (storyboard) {
      setTimeout(() => {
        const version = StoryboardVersion.mapFromStoryboard(storyboard);
        const data = JSON.stringify(version);
        const lastIndex = this.previousVersions.length - 1;
        if (data !== this.previousVersions[lastIndex]) {
          this.previousVersions.push(data);

          // detect changes cause renderer is running outside of zone
          if (fromRender) {
            this.ref.tick();
          }
        }
      }, 200);
    }
  }

  public undo(): void {
    const lastIndex = this.previousVersions.length - 1;
    this.previousVersions.pop();
    const data =  this.previousVersions[lastIndex - 1];
    const version = JSON.parse(data) as StoryboardVersion;

    const timeline = Timeline.fromJson(version.timeline);

    this.storyboard.timeline.layers = timeline.layers;

    this.storyboardSub.next(this.storyboard);
  }

  public clearHistory(): void {
    this.previousVersions = [];
    this.storyboard = null;
  }

  getPxTimeRatio(): number {
    return this.pxTimeRatio;
  }
  /**
   * @todo
   *    ha ezt átállítjuk, az korrigálja a setTimelinePosition()-t is
   *    - ha rázoomolunk, majd drageljük a timeline-t jobbra a határig, majd visszazoomolunk, akkor a megrövidült timeline miatt a timeline jobb szélén túl fogunk látni
   */
  setZoom(zoom: number) {
    this.zoom = zoom;
    this.zoomChangeEvent.next(this.zoom);
  }

  public convertTimeToPx(milliseconds: number) {
    return Math.floor(milliseconds * (this.pxTimeRatio * this.zoom));
  }

  public convertPxToTime(px: number) {
    return px / this.pxTimeRatio / this.zoom;
  }

  public zoomIn() {
    this.setZoom(Math.min(this.zoom + this.zoomStep, this.maxZoom));
  }

  public isZoomInDisabled() {
    return this.zoom >= this.maxZoom;
  }

  public zoomOut() {
    this.setZoom(Math.max(this.zoom - this.zoomStep, this.minZoom));
  }

  public isZoomOutDisabled() {
    return this.zoom <= this.minZoom;
  }

  isScrollEnabled() {
    return this.scrollEnabled;
  }

  setTimelineWidthInPx(value: number) {
    this.timelineWidthInPx = value;
  }

  getTimelineWidthInPx() {
    return this.timelineWidthInPx;
  }

  public setScrollEnabled(status: boolean) {
    this.scrollEnabled = status;
  }

  public getZoom() {
    return this.zoom;
  }

  getTimelinePosition() {
    return this.timelinePosition;
  }
  /**
   * @todo
   *   ugyanazokat a korlátokat rárakni, mint amit a lenti timeline is használ
   *   - ne lehessen balra túlmenni
   *   - ne lehessen jobbra túlmenni
   * @todo
   *   ha ezt átállítjuk, arra reagáljon a lenti timeline is
   */
  setTimelinePosition(value: number) {
    const storyboardLengthDiff = this.convertTimeToPx(this.storyboard.getDuration().as_milliseconds)
        - this.getTimelineWidthInPx();

    if (storyboardLengthDiff <= 0) {
      this.timelinePosition = 0;
    } else {
      this.timelinePosition = Math.max(-storyboardLengthDiff, Math.min(0, value));
    }

    const elements = this.collectLayerElements();
    for (let i = 0; i < elements.length; i++) {
      elements[i].nativeElement.style.left = this.timelinePosition + 'px';
    }
  }

  collectLayerElements() {
    const elements = [];
    for (let i = 0; i < this.storyboard.timeline.layers.length; i++) {
      elements.push(this.storyboard.timeline.layers[i].getElementRef());
    }

    return elements;
  }

  addToTimelinePosition(value: number) {
    this.setTimelinePosition(this.timelinePosition + value);
  }

  isExtending() {
    return this.isClipExtending;
  }
}



// WEBPACK FOOTER //
// ./src/app/storyboard/storyboard-edit/timeline/timeline.service.ts